import React, { useCallback, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { styled } from '@mui/material/styles';
import { Textarea } from './text/Textarea';
import { Attach } from './attach/Attach';
import { RecordAudioButton } from './audio/RecordAudioButton';
import { Preview } from './preview/Preview';
import { Recording } from './audio/Recording';
import { SubmitError } from './error/SubmitError';
import { formMessages } from './i18n';
import { useSubmitFormState } from './hooks';
import { useAttachMedia, useFileDrop } from './attach/hooks';
import { attachMessages } from './attach/i18n';
import { FileDragContext } from './attach/dragContext';

import { isAttachUiVisible, MessageType } from '@enuan/chat__utils';
import { configurationContext } from '../../contexts/configuration';
import { submitContext } from '../../contexts/submit.context';
import { configContext } from '../../contexts/config/context';
import { instanceContext } from '../../contexts/instance/context';
import { ChannelState } from 'src/interfaces';
import { Button } from '../UI/Button/Button';

import { useLogError } from '../ErrorBoundary/useLogError';
import { grow } from 'src/theme/mixins';
import { ReactComponent as SendIcon } from '@/assets/send.svg';
import { ReactComponent as AttachIcon } from '@/assets/attach.svg';

// ── SubmitForm ───────────────────────────────────────────────────────

/**
 * Main message-composition form.
 *
 * Provides the {@link submitContext} to all children and orchestrates
 * text input, file attachment, audio recording, and message submission.
 *
 * The `submitContext.Provider` is placed as the outer wrapper so that inner
 * components (e.g. `FormWithDrop`) can consume the context correctly.
 * Drag-and-drop file attachment is handled by `FormWithDrop`, which reads
 * the context and wires {@link useFileDrop} to the `<form>` element.
 *
 * When the channel is closed, only a "reopen chat" button is rendered.
 */
export const SubmitForm = styled(
	({ className }: { className?: string }) => {
		const { file_upload, speech_input, attach_ui } =
			useContext(configurationContext);
		const { isInputLocked, channelState } = useContext(configContext);
		const { restart } = useContext(instanceContext);
		const intl = useIntl();

		const {
			contextValue,
			recorder,
			showTextarea,
			isRecordAudioTempDisabled,
			isAttachDisabled,
			isSubmitDisabled,
			onFormSubmit,
		} = useSubmitFormState(isInputLocked ?? false);

		if (channelState === ChannelState.CHANNEL_CLOSED) {
			return (
				<form
					onSubmit={onFormSubmit}
					className={classNames(className, 'footer__form', 'footer__closed')}
				>
					<Button onClick={restart} color="secondary">
						{intl.formatMessage(formMessages.closed__start_new)}
					</Button>
				</form>
			);
		}

		const disabledSubmit = isInputLocked || isSubmitDisabled;
		const disabledAttach = isInputLocked || isAttachDisabled;

		// The Provider must wrap FormWithDrop so that useAttachMedia / useFileDrop
		// (called inside FormWithDrop) can read the correct context state.
		return (
			<submitContext.Provider value={contextValue}>
				<FormWithDrop
					className={classNames(
						className,
						'footer__form',
						!showTextarea && 'footer__form--preview',
					)}
					onSubmit={onFormSubmit}
				>
					{contextValue.error ? (
						<SubmitError />
					) : (
						<>
							{file_upload?.enabled && isAttachUiVisible({ attach_ui }) && (
								<Attach disabled={disabledAttach} />
							)}

							<div className="form__content">
								{/*
								 * Both nodes live in the DOM at all times so that
								 * show/hide is driven purely by CSS (via the
								 * footer__form--dragover class on the <form>).
								 * This ensures the drop hint and the textarea
								 * swap atomically in the same browser paint frame,
								 * with no React async re-render delay.
								 */}
								<div className="form__content-inner">
									<FormContent showTextarea={showTextarea} />
								</div>
								<DropHint />
							</div>

							<div className="form__actions">
								{speech_input && recorder.isSupported && (
									<RecordAudioButton
										disabled={isInputLocked || isRecordAudioTempDisabled}
										startRecording={recorder.start}
										stopRecording={recorder.stop}
									/>
								)}
								<button
									type="submit"
									className={classNames(
										'form__action',
										disabledSubmit && 'form__action--disabled',
										'form__submit',
									)}
									disabled={disabledSubmit}
								>
									<SendIcon className="icon send" />
								</button>
							</div>
						</>
					)}
				</FormWithDrop>
			</submitContext.Provider>
		);
	},
	{
		target: 'SubmitForm-root',
		label: 'SubmitForm',
	},
)({
	width: '100%',
	flex: 1,
	minHeight: '5.2em',
	padding: 0,
	margin: 0,

	display: 'flex',
	justifyContent: 'space-between',
	gap: '1.3em',

	'&.footer__form--preview': {
		maxHeight: 'unset',
	},
	'&.footer__closed': {
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		'& .button': {
			fontSize: '1.3em',
		},
	},
	'& .form__content': {
		flex: 1,
		position: 'relative',
		marginTop: '1em',
		marginBottom: '1em',
		display: 'flex',
		overflowX: 'hidden',
		'&:first-child': {
			marginLeft: '32px',
		},
		'& .form__content-inner': {
			display: 'flex',
			flex: 1,
			width: '100%',
			opacity: 1,
			transition: 'opacity 80ms ease',
		},
	},
	'& .form__actions': {
		marginLeft: 'auto',
		display: 'flex',
		alignItems: 'flex-end',
		marginRight: '1.3em - 0.5em',
	},
	'& .form__action': {
		padding: '0.5em',
		transition: 'all 150ms',
		userSelect: 'none',
		border: 0,

		'& .icon': {
			width: '20px',
			height: '20px',
		},
		'& .st0': {
			fill: 'var(--chat__footer__form__action_color)',
		},
		'&.form__action--disabled,&.form__action:disabled': {
			'& .st0': {
				fill: 'var(--chat__footer__form__action--disabled_color)',
			},
		},
		'&:not(.form__action--disabled):not(:disabled)': {
			cursor: 'pointer',
			...grow(
				{
					scale: 1.25,
					positionX: '50%',
					positionY: '100%',
				},
				{
					'& .st0': {
						fill: 'var(--chat__footer__form__action--hover_color)',
					},
				},
			),
		},
	},
	'& .form__submit': {
		'& .st0': {
			stroke: 'none',
			fill: 'var(--chat__footer__form__submit_color)',
		},
		'&:not(.form__action--disabled):not(:disabled)': {
			transform: 'scale(1.25)',
			...grow({ scale: 1.5, positionX: '50%', positionY: '100%' }),
		},
	},

	// DropHint is hidden by default; form__content-inner is visible.
	// Transition on opacity ensures a smooth swap in both directions.
	'& .form__drop-hint': {
		display: 'flex',
		position: 'absolute',
		inset: 0,
		opacity: 0,
		pointerEvents: 'none',
		transition: 'opacity 80ms ease',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '0.5em',
		color: 'var(--chat__footer__form__action_color)',
		userSelect: 'none',
		'& .drop-hint__icon': {
			width: '1.4em',
			height: '1.4em',
			flexShrink: 0,
			'& .st0': {
				fill: 'var(--chat__footer__form__action_color)',
			},
		},
		'& .drop-hint__label': {
			fontSize: '1em',
			fontWeight: 600,
			whiteSpace: 'nowrap',
		},
	},
	// When a file drag is detected over the frame (set by FileDragContext),
	// highlight the form boundary and swap content/hint atomically via CSS.
	// All transitions are driven by the class on the <form> element so the
	// outline, hint, and button changes happen in the same browser paint.
	'&.footer__form--dragover': {
		outline: '2px dashed var(--chat__footer__form__action_color)',
		outlineOffset: '-3px',
		borderRadius: '6px',
		backgroundColor:
			'color-mix(in srgb, var(--chat__footer__form__action_color) 6%, var(--chat__footer_background))',
		'& .form__content-inner': {
			opacity: 0,
			pointerEvents: 'none',
		},
		'& .form__drop-hint': {
			opacity: 1,
		},
		'& .form__attach': {
			opacity: 0,
			transition: 'opacity 80ms ease',
		},
		'& .form__actions': {
			opacity: 0,
			transition: 'opacity 80ms ease',
		},
	},
});

// ── FormWithDrop ─────────────────────────────────────────────────────

type FormWithDropProps = {
	className?: string;
	onSubmit: (e: React.SyntheticEvent) => void;
	children: React.ReactNode;
};

/**
 * Inner `<form>` wrapper rendered inside `submitContext.Provider`.
 *
 * Calls {@link useAttachMedia} and {@link useFileDrop} — which both read from
 * `submitContext` — so they correctly observe the current pending-message state.
 * Spreads the resulting drop event handlers on the `<form>` element so that
 * the form acts as the actual drop target (file processing happens here).
 *
 * The visual drag state (`footer__form--dragover`) is driven by
 * {@link FileDragContext} — populated at the `ChatMessages` level — so the
 * form border highlight appears as soon as the drag enters the chat frame,
 * not only when the pointer is directly over the form.
 *
 * The drop-zone text hint is rendered in `form__content` by the parent
 * `SubmitForm` component (replacing `FormContent` when dragging), so the
 * Attach and Send buttons remain visible and accessible during a drag.
 */
const FormWithDrop = ({ className, onSubmit, children }: FormWithDropProps) => {
	const logError = useLogError('SubmitForm');
	const { attachFile, canAttachMedia } = useAttachMedia();

	// Frame-level drag detection: drives the border-highlight modifier class.
	const { isDraggingFileOver } = useContext(FileDragContext);

	useMediaClearOnKey();

	/**
	 * Stable callback forwarded to {@link useFileDrop}.
	 *
	 * Memoised so that `useFileDrop`'s internal `onDrop` handler is not
	 * recreated on every render due to an unstable `onFile` reference.
	 */
	const onFile = useCallback(
		(file: File) => {
			attachFile(file).catch((error) => logError('onDrop', { error }));
		},
		[attachFile, logError],
	);

	// `dropHandlers` makes the <form> the actual drop target: calls
	// preventDefault on dragover and processes the file on drop.
	const { dropHandlers } = useFileDrop({
		enabled: canAttachMedia,
		onFile,
	});

	return (
		<form
			{...dropHandlers}
			onSubmit={onSubmit}
			className={classNames(
				className,
				isDraggingFileOver && 'footer__form--dragover',
			)}
		>
			{children}
		</form>
	);
};

// ── FormContent ─────────────────────────────────────────────────────

type FormContentProps = {
	showTextarea: boolean;
};

/**
 * Renders the appropriate content inside the form area based on
 * the current submit state: locked message, textarea,
 * recording indicator, or attachment preview.
 *
 * Error state is handled upstream by `SubmitError` and is never
 * reached here.
 */
const FormContent = ({ showTextarea }: FormContentProps) => {
	const { isInputLocked } = useContext(configContext);
	const { action, message } = useContext(submitContext);
	const intl = useIntl();

	if (isInputLocked)
		return <span>{intl.formatMessage(formMessages.input_locked)}</span>;

	if (showTextarea) return <Textarea />;

	if (action === 'recording' && !message) return <Recording />;

	return <Preview />;
};

// ── DropHint ─────────────────────────────────────────────────────────

/**
 * Drop-zone label rendered inside `form__content` while a file drag is active.
 *
 * Replaces the textarea (or preview) in the form's centre column so the hint
 * is visible without covering the Attach button or the Send button.
 * The icon and label colors are driven by CSS via `--chat__footer__form__action_color`.
 */
const DropHint = () => {
	const intl = useIntl();
	return (
		<span className="form__drop-hint">
			<AttachIcon className="drop-hint__icon" />
			<span className="drop-hint__label">
				{intl.formatMessage(attachMessages.drop_hint)}
			</span>
		</span>
	);
};

// ── useMediaClearOnKey ────────────────────────────────────────────────

/**
 * Listens for `Delete` / `Backspace` at the document level and clears a
 * pending media attachment when one is present.
 *
 * The handler is a no-op when:
 * - No message is pending, or the pending message is not a media attachment.
 * - The event target is a text input or textarea (to avoid interfering with
 *   typing elsewhere on the page).
 *
 * Called inside {@link FormWithDrop} so it is only active while the submit
 * form is mounted and has access to `submitContext`.
 */
const useMediaClearOnKey = () => {
	const { message, setMessage, setError } = useContext(submitContext);

	useEffect(() => {
		/**
		 * Clears the pending media message when Delete or Backspace is pressed,
		 * provided no text input has focus.
		 */
		const handler = (e: KeyboardEvent) => {
			if (e.key !== 'Delete' && e.key !== 'Backspace') return;
			if (message?._type !== MessageType.MEDIA) return;
			// Leave the event alone if the user is editing text somewhere else.
			const tag = (e.target as HTMLElement).tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA') return;
			setMessage(undefined);
			setError(undefined);
		};

		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [message, setMessage, setError]);
};
